/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useRef, useState, useEffect, version, useMemo } from 'react';
import Highcharts, { SeriesOptionsType } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './DeploymentProgressTrend.css';
import { SelectDropDown, useAppDispatch, useAppSelector, useEffectOnce } from '../../../../globalUtils/globalExports';
import { formatNumberWithCommas } from '../../../../globalUtils/globalHooks';
import { format, parse, isValid } from 'date-fns';
import { RectangularSkeleton } from '../../../../globalUtils/SkeletonDesign/SkeletonDesign';
import { Icon } from '../../../../globalUtils/globalIcons';
import { getAcReferenceDataReducer, getAcVersionReleasesReducer } from '../../../rmsReduxStore/acReduxExports';
import { getReferenceDataReducer } from '../../../../rmsReduxStore/reduxExports';
import { getAcFilterSelectedValue, setAcFilterData } from '../../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import CustomPulseLoader from '../../../../components/softwareReleaseManagement/dashboard/CustomPulseLoader';

interface DeploymentProgressTrendProp {
    handleFilter: Function,
    getFilterValue: Function,
    fromPopUp?:boolean,
    popupHandler:React.Dispatch<React.SetStateAction<boolean>>
}

const DeploymentProgressTrend: React.FC<DeploymentProgressTrendProp> = (props) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    const { handleFilter, getFilterValue,fromPopUp,popupHandler} = props;
    const { versionReleasesDateRangeReferenceData } = useAppSelector(getReferenceDataReducer);
    const { deploymentProgressTrendInfo,deploymentProgressTrendLoader } = useAppSelector(getAcVersionReleasesReducer);
    const [filterValue, setFilterValue] = useState<IGeneralReferenceData | null>(null);
    const dispatch = useAppDispatch();
    const navigationUpperLimit = 95;
    const [visibleMin, setVisibleMin] = useState(0);
    const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
    const chartHeight = fromPopUp ? 600 : 300;

    useEffectOnce(() => {
        setFilterValue(getFilterValue('swVersionDateInterval'));
    });

    useEffect(()=>{
        setFilterValue(getAcFilterSelectedValue('swVersionDateInterval','versionReleasesDashboard',false));
    },[deploymentProgressTrendInfo])

    /**
   * Converts HSL color values to a HEX string.
   * @param hue - Hue (0–360)
   * @param saturation - Saturation percentage (0–100)
   * @param lightness - Lightness percentage (0–100)
   * @returns HEX color string (e.g. "#aabbcc")
   */
    const hslToHex = (hue: number, saturation: number, lightness: number): string => {
        saturation /= 100;
        lightness /= 100;

        const k = (n: number) => (n + hue / 30) % 12;
        const chroma = saturation * Math.min(lightness, 1 - lightness);
        const component = (n: number) =>
            lightness - chroma * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

        const toHex = (value: number) =>
            Math.round(value * 255).toString(16).padStart(2, '0');

        const hexColor = `#${toHex(component(0))}${toHex(component(8))}${toHex(component(4))}`;
        return hexColor;
    }

    /**
     * Generates a stable HEX color for a given string.
     * Same string will always map to the same color.
     *
     * @param text - Input string (e.g. category name)
     * @param saturation - (optional) saturation % for vividness
     * @param lightness - (optional) lightness % for brightness
     * @returns HEX color string
     */
    const generateColorFromString = (
        text: string,
        saturation: number = 65,
        lightness: number = 55
    ): string => {
        // FNV-1a 32-bit hash for stable distribution
        let hash = 2166136261 >>> 0;
        for (let i = 0; i < text.length; i++) {
            hash ^= text.charCodeAt(i);
            hash = Math.imul(hash, 16777619) >>> 0;
        }
        const hue = hash % 360; // Map hash to a hue (0–359)
        return hslToHex(hue, saturation, lightness);
    }

    function formatTimeStamp(rawTimeStamp: string,isTooltip:boolean): string {
         const date = new Date(rawTimeStamp);
         const day = date.getDate().toString();
         const month = date.toLocaleString('en-US', { month: 'short' });
         const dateString = day + ' ' + month;
         if(isTooltip){
            const year  = date.getFullYear()
            const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
            return (dayName + ', ' + dateString + ', ' + year)
         }
         return dateString;
    }

    function compareVersions(a: string, b: string): number {
        const parseVersion = (v: string) =>
            v.match(/\d+/g)?.map(Number) || [];

        const partsA = parseVersion(a);
        const partsB = parseVersion(b);

        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
            const diff = (partsA[i] || 0) - (partsB[i] || 0);
            if (diff !== 0) return diff;
        }
        return 0;
    }

    const getFormattedData = useCallback(() => {
        const data = deploymentProgressTrendInfo || [];

        // // apply filter if any
        // if (filterValue && filterValue.value && filterValue.value !== 'All') {
        //   data = data.filter((item: any) => item.version === filterValue.value);
        // }

        const slicedData = data.slice(visibleMin, visibleMax);

        const categories = slicedData.map((item: any) => item?.date);
        const allVersions: string[] = [];
        slicedData?.forEach((item) => {
            item?.values.forEach((v: any) => {
                if (!allVersions.includes(v.softwareVersion)) {
                   allVersions.push(v.softwareVersion)
                }
            });
        });

        allVersions.sort(compareVersions).reverse()

        const seriesMap: Record<string, any[]> = {};
        allVersions.forEach((version) => {
            seriesMap[version] = Array(slicedData.length).fill(0);
        });

        slicedData.forEach((entry, idx) => {
            entry.values.forEach((v: any) => {
                seriesMap[v.softwareVersion][idx] = {
                    y: v.chargersCount ?? 0,
                    percentage: Number(v?.percentage.toFixed(2)) ?? 0 ,
                    date: categories[idx]
                };
            });
        });


        const series = Array.from(allVersions).map((version) => ({
            name: version,
            type: 'column',
            data: seriesMap[version],
            color: generateColorFromString(version),
            pointPlacement: 'between',
        }));


        return { categories, series };
    }, [deploymentProgressTrendInfo, visibleMin, visibleMax, filterValue]);


    const dataLength = useMemo(()=>{
        return deploymentProgressTrendInfo?.length ?? 0;
      },[deploymentProgressTrendInfo])

    const handleNavigation = (direction: 'left' | 'right') => {
    const step = navigationUpperLimit ;
    if (direction === 'left') {
      setVisibleMin(Math.max(visibleMin - step, 0));
      setVisibleMax(Math.max(visibleMax - step, step));
    } else {
      setVisibleMin(Math.min(visibleMin + step, dataLength - step));
      setVisibleMax(Math.min(visibleMax + step, dataLength ));
    }
  };

    const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
        const rawValue = this.value;
        return `<div style="text-align:center;">${rawValue}</div>`;
    };

    const { categories, series } = getFormattedData()
    let labelStep = 1;
    const totalBars = categories.length;
    if(totalBars > 60) labelStep = 8
    else if (totalBars > 20) labelStep = 4;
    else if (totalBars > 10) labelStep = 2;


    const options: Highcharts.Options =useMemo(()=>{
       return {
        chart: {
            type: 'column',
            backgroundColor: 'transparent',
            height: chartHeight,
            spacing: [20, 25, 0, 0],
            events: {
                render: function () {
                    const chart = this as Highcharts.Chart & any;

                    // Remove existing custom arrows before adding new ones
                    const existingArrows = chart.container.getElementsByClassName('custom-arrow');
                    while (existingArrows.length > 0) {
                        existingArrows[0].remove();
                    }

                    // Left arrow (shifted more inside by changing left position)
                    if (visibleMin > 0) {
                        chart.renderer.image(
                            '/images/left_arrow_highchart.svg',
                            chart.plotLeft - 25, // shifted inside
                            chart.plotTop + chart.plotHeight / 2 - 10,
                            20,
                            20
                        )
                            .addClass('custom-arrow left')
                            .on('click', () => handleNavigation('left'))
                            .add();
                    }

                    // Right arrow (shifted more inside by changing left position)
                    if (visibleMax < dataLength - 1) {
                        chart.renderer.image(
                            '/images/right_arrow_highchart.svg',
                            chart.plotLeft + chart.plotWidth - 0, // shifted inside
                            chart.plotTop + chart.plotHeight / 2 - 10,
                            20,
                            20
                        )
                            .addClass('custom-arrow right')
                            .on('click', () => handleNavigation('right'))
                            .add();
                    }
                }
            }
        },
        title: { text: undefined },
        subtitle: { text: undefined },
        xAxis: {
            title: {
                text: 'Software Release Date'
            },
            gridLineDashStyle: 'ShortDash',
            categories: categories.map(date=>formatTimeStamp(date,false)),
            lineColor: '#005EFF',
            tickColor: '#005EFF',
            labels: {
                rotation: -45,
                step: labelStep,
                formatter: xAxisLabelFormatter,
            },
        },
        yAxis: {
            title: { text: 'Number of Chargers' },
            gridLineDashStyle: 'ShortDash',
            min: 0,
        },
        tooltip: {
            formatter: function () {
                const date = (this.point as any).date;
                const value = this.y;
                const percentage = (this.point as any).percentage.toFixed(2)
                // \u25CF ---> ●
                return `<span style="font-size:12px">${formatTimeStamp(date,true)}</span><br/>
        <span style="color:${this.point.color}">\u25CF</span>
        ${this.point.series.name}: <b>${formatNumberWithCommas(value || 0)} chargers (${percentage}%)</b>`;
            },
        },
        legend: { enabled: false },
        plotOptions: {
            column: {
                stacking: 'normal',
                pointPadding: 0,
                groupPadding: 0.1,
                borderWidth: 0,
                borderRadius: 0 ,
                crisp:false,
            },
        },
        series: series as SeriesOptionsType[],
        credits: { enabled: false },
        exporting: { enabled: false },
    }
},[deploymentProgressTrendInfo,visibleMax,visibleMin]);

    const handleDeploymentProgressTrendFilter =
        async (val: any, filterKey: string): Promise<void> => {
            const res = await setAcFilterData(val, filterKey, 'versionReleasesDashboard', 'SET', false)(dispatch);
            if (res?.message === 'Action dispatched successfully') {
                handleFilter(res?.filters);
            }
        }

    const handleSelectChange = (val: any): void => {
        setFilterValue(val);
        handleDeploymentProgressTrendFilter(val, 'swVersionDateInterval');
        handleChartPageReset();
    };

    const handleChartPageReset = () => {
        setVisibleMin(0);
        setVisibleMax(navigationUpperLimit);
    };

    const preventClickEventPropagation = (e)=>{
        e.stopPropagation();
    }

    return (
        <div className={fromPopUp ?'deployment-progress-chart-popup' :'deployment-progress-chart'} 
        onClick={preventClickEventPropagation}>
            <div className="chart-header">
                <div className="deployment-progress-chart-header">
                    <div className='deployment-progress-chart-header-heading'>SW Deployment Progress Over Time</div>
                     <div className='deployment-progress-chart-expansion-icon'
                      onClick={()=>popupHandler(prev=>!prev)}>
                        {
                            fromPopUp ? <Icon
                        containerClassName="deployment__chart__shrink-icon"
                            iconClassName="side__popover__shrink__icon"
                    /> :
                    <Icon
                        containerClassName="deployment__chart__expand-icon"
                            iconClassName="side__popover__expand__icon"
                    />
                        }
                     </div>
                </div>
                <div className="chart-filters">
                    <SelectDropDown
                        selectValue={filterValue}
                        selectOptions={versionReleasesDateRangeReferenceData ?? []}
                        handleSelectOptionChange={handleSelectChange}
                        selectDropDownId="deployment-progress-trend-date-range-filter-dropdown"
                        dropDownClassName="deployment-progress-trend-date-range-filter-dropdown"
                        isMultiSelect={false}
                        placeholder={'Date'}
                    />
                </div>
            </div>
            <div className="deployment-progress-trend-chart-body">
                {(deploymentProgressTrendLoader || !deploymentProgressTrendInfo) ? (
                    <div className="deployment-progress-trend-chart-loader" style={{ height: `${chartHeight}px` }}>
                        <CustomPulseLoader showLoader={true} size={fromPopUp?40:20}   />
                    </div>
                ) : deploymentProgressTrendInfo?.length === 0 ? (
                    <div className="no-data-text" style={{ height: `${chartHeight}px` }}>No data available</div>
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
                )}
            </div>
        </div>
    );
};

export default DeploymentProgressTrend;